import React, { useEffect, useState } from "react";
import { X, Clock, User, ArrowUpCircle, ArrowDownCircle, Ban } from "lucide-react";
import { api } from "@/lib/api";
import { useMobile } from "./MobileApp";
import { Empty, Glass, Rise, SectionTitle, Spinner, clp, hora } from "./ui";

const Dif = ({ v }) => v == null ? null : (
  <span className={`font-mono text-xs font-bold px-2 py-0.5 rounded-full border ${Math.abs(v) < 1 ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" : v < 0 ? "text-rose-400 border-rose-500/30 bg-rose-500/10" : "text-amber-400 border-amber-500/30 bg-amber-500/10"}`}>
    {Math.abs(v) < 1 ? "Cuadra" : `${v < 0 ? "Faltan" : "Sobran"} ${clp(Math.abs(v))}`}
  </span>
);

const Detalle = ({ id, onClose }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/mobile/cajas/${id}`).then((r) => setD(r.data)); }, [id]);
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end" onClick={onClose} data-testid="caja-detalle">
      <div className="w-full max-w-md mx-auto max-h-[85dvh] overflow-y-auto rounded-t-3xl bg-[#0B1324] border-t border-white/10 p-5 pb-10" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center"><h3 className="font-bold font-display">Detalle del turno</h3>
          <button onClick={onClose} data-testid="caja-detalle-close" className="w-8 h-8 rounded-full grid place-items-center bg-white/5"><X className="w-4 h-4" /></button></div>
        {!d ? <Spinner /> : (<>
          <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
            {Object.entries(d.medios).map(([k, v]) => (
              <div key={k} className="rounded-xl bg-[#0F1A30] p-3"><p className="text-slate-500">{k}</p><p className="font-mono font-bold text-sm">{clp(v)}</p></div>
            ))}
          </div>
          <SectionTitle>Movimientos de caja</SectionTitle>
          {d.movimientos.length ? d.movimientos.map((m) => (
            <div key={m.movement_id} className="flex items-center gap-3 py-2 border-b border-white/5 text-sm" data-testid={`caja-mov-${m.movement_id}`}>
              {m.tipo === "INGRESO" ? <ArrowDownCircle className="w-4 h-4 text-cyan-400" /> : <ArrowUpCircle className="w-4 h-4 text-rose-400" />}
              <span className="flex-1 truncate">{m.motivo}</span><span className="font-mono">{clp(m.monto)}</span>
            </div>
          )) : <p className="text-xs text-slate-500">Sin ingresos ni egresos.</p>}
          <SectionTitle>Anulaciones</SectionTitle>
          {d.anuladas.length ? d.anuladas.map((v) => (
            <div key={v.numero} className="flex items-center gap-3 py-2 border-b border-white/5 text-sm">
              <Ban className="w-4 h-4 text-rose-400" /><span className="flex-1 truncate">#{v.numero} {v.motivo_anulacion || ""}</span><span className="font-mono">{clp(v.total)}</span>
            </div>
          )) : <p className="text-xs text-slate-500">Sin anulaciones.</p>}
        </>)}
      </div>
    </div>
  );
};

const Caja = ({ c, i, onOpen }) => (
  <Rise i={i}>
    <button onClick={onOpen} data-testid={`caja-card-${c.shift_id}`} className={`w-full text-left rounded-2xl border bg-[#0B1324] p-4 transition-colors duration-200 hover:bg-[#0F1A30] ${c.estado === "ABIERTA" ? "border-cyan-500/30" : "border-white/[0.08]"}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${c.estado === "ABIERTA" ? "bg-emerald-400 shadow-[0_0_10px_#34D399] animate-pulse" : "bg-slate-600"}`} />
          <p className="font-semibold">{c.caja}</p>
        </div>
        {c.estado === "ABIERTA" ? <span className="text-[10px] font-bold tracking-widest text-emerald-400">ABIERTA</span> : <Dif v={c.diferencia} />}
      </div>
      <p className="text-[11px] text-slate-500 mt-1">{c.store_nombre}</p>
      <div className="mt-3 flex items-end justify-between">
        <div className="text-[11px] text-slate-400 space-y-0.5">
          <p className="flex items-center gap-1"><User className="w-3 h-3" />{c.abre || "—"}{c.cierra && c.cierra !== c.abre ? ` → ${c.cierra}` : ""}</p>
          <p className="flex items-center gap-1 font-mono"><Clock className="w-3 h-3" />{hora(c.opened_at)}{c.closed_at ? ` – ${hora(c.closed_at)}` : ""}</p>
        </div>
        <div className="text-right"><p className="font-mono font-bold text-lg">{clp(c.ventas_total)}</p><p className="text-[10px] text-slate-500">{c.tickets} tickets · egresos {clp(c.retiros)}</p></div>
      </div>
    </button>
  </Rise>
);

export default function Cajas() {
  const { me, storeId } = useMobile();
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(null);
  useEffect(() => {
    const load = () => api.get("/mobile/cajas", { params: { store_id: storeId || undefined } }).then((r) => setD(r.data.cajas));
    load(); const t = setInterval(load, 30000); return () => clearInterval(t);
  }, [storeId]);
  if (!d) return <Spinner />;
  const abiertas = d.filter((c) => c.estado === "ABIERTA");
  const cerradas = d.filter((c) => c.estado !== "ABIERTA");
  const open = (c) => { if (me.tier === "PRO") setSel(c.shift_id); };
  return (
    <div className="pt-5" data-testid="mobile-cajas">
      <h1 className="text-2xl font-bold font-display">Cajas</h1>
      <p className="text-xs text-slate-400 mt-1">{abiertas.length} abierta(s) · {cerradas.length} cierre(s) hoy{me.tier !== "PRO" ? " · detalle de turno en Pro" : ""}</p>
      <SectionTitle>Abiertas ahora</SectionTitle>
      <div className="space-y-2">{abiertas.length ? abiertas.map((c, i) => <Caja key={c.shift_id} c={c} i={i} onOpen={() => open(c)} />) : <Empty>No hay cajas abiertas.</Empty>}</div>
      <SectionTitle>Cerradas hoy</SectionTitle>
      <div className="space-y-2">{cerradas.length ? cerradas.map((c, i) => <Caja key={c.shift_id} c={c} i={i} onOpen={() => open(c)} />) : <Empty>Aún no hay cierres hoy.</Empty>}</div>
      {sel && <Detalle id={sel} onClose={() => setSel(null)} />}
    </div>
  );
}
